import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../firestore"

export const SpaceThumbnailSettingDocument = z.object({
  home: z.object({
    selection_rules: z.object({
      follow_user_percentage: z.int().min(0).max(100)
        .describe("フォローユーザーグループのスペースをどれだけホームに表示されせるのかの割合(%)。"),
      follow_user_started_at_percentage: z.int().min(0).max(100)
        .describe("フォローユーザーグループのスペースのうち開始時刻が新しい順でどれだけホームに表示させるかの割合(%)。"),
      follow_user_current_live_count_percentage: z.int().min(0).max(100)
        .describe("フォローユーザーグループのスペースのうちでどれだけホームに表示させるかの割合(%)。"),
      pickup_live_percentage: z.int().min(0).max(100)
        .describe("ピックアップライブのスペースをどれだけホームに表示させるのかの割合(%)。"),
      pickup_live_started_at_percentage: z.int().min(0).max(100)
        .describe("ピックアップライブのうち開始時刻が新しい順でどれだけ選出するかの割合(%)。"),
      pickup_live_current_live_count_percentage: z.int().min(0).max(100)
        .describe("ピックアップライブのうち同時視聴者数が多い順でどれだけ選出するかの割合(%)。"),
      live_count_ranking_count_percentage: z.int().min(0).max(100)
        .describe("同時視聴者数ランキングのスペースをどれだけホームに表示させるのかの割合(%)。"),
    }),
    first_launch: z.object({
      viewer_count_threshold: z.number().min(0)
        .describe("初回起動ユーザー向けライブの同時視聴者数の上限（この値未満のライブを対象とする）。"),
      account_created_within_days: z.number().min(0)
        .describe("配信者のアカウント作成からの期間の上限（日）。この期間内のアカウントを対象とする。"),
      owner_follower_count_min: z.number().min(0)
        .describe("配信者のフォロワー数の下限。"),
      owner_follower_count_max: z.number().min(0)
        .describe("配信者のフォロワー数の上限。"),
    }).optional(),
    thumbnail_size_rules: z.object({
      large_percentage: z.int().min(0).max(100)
        .describe("ホームに表示するスペース全体でサムネイルサイズを大にする割合(%)。"),
      medium_percentage: z.int().min(0).max(100)
        .describe("ホームに表示するスペース全体でサムネイルサイズを中にする割合(%)。"),
      small_percentage: z.int().min(0).max(100)
        .describe("ホームに表示するスペース全体でサムネイルサイズを小にする割合(%)。"),
      large_count: z.number().min(0).max(20)
        .describe("スペース数が20件以下の場合にサムネイルサイズを大にするスペース数"),
      medium_count: z.number().min(0).max(20)
        .describe("スペース数が20件以下の場合にサムネイルサイズを中にするスペース数"),
      follow_user_large_percentage: z.int().min(0).max(100)
        .describe("フォローユーザーグループのスペース内でサムネイルサイズを大にする割合(%)。"),
      follow_user_medium_percentage: z.int().min(0).max(100)
        .describe("フォローユーザーグループのスペース内でサムネイルサイズを中にする割合(%)。"),
      follow_user_small_percentage: z.int().min(0).max(100)
        .describe("フォローユーザーグループのスペース内でサムネイルサイズを小にする割合(%)。"),
      live_count_ranking_large_percentage: z.int().min(0).max(100)
        .describe("同時視聴者数ランキングのスペース内でサムネイルサイズを大にする割合(%)。"),
      live_count_ranking_medium_percentage: z.int().min(0).max(100)
        .describe("同時視聴者数ランキングのスペース内でサムネイルサイズを中にする割合(%)。"),
      live_count_ranking_small_percentage: z.int().min(0).max(100)
        .describe("同時視聴者数ランキングのスペース内でサムネイルサイズを小にする割合(%)。"),
    }),
    look_distance_threshold: z.object({
      far: z.number().min(0),
      near: z.number().min(0),
    }).describe("ルックの被写体距離選択に使用する閾値。"),
    background_effect_intensity_elapsed_time: z.object({
      live: z.object({
        under: z.number(),
        over: z.number(),
      }),
      active_space: z.object({
        under: z.number(),
        over: z.number(),
      }),
    }).describe("どのエフェクトの強さを背景画像に適用するかを判定するための経過時間（ミリ秒）。"),
    overflow_height: z.object({
      power_duration_ms: z.number().min(0)
        .describe("はみ出し幅のsmall判定に使用するパワー送信件数の集計期間（ミリ秒）。"),
      total_price_duration_ms: z.number().min(0)
        .describe("はみ出し幅のlarge/medium判定に使用するパワー金額累計の集計期間（ミリ秒）。"),
      total_price_threshold: z.number().min(0)
        .describe("はみ出し幅をlargeにするのに必要なパワーの累計金額。"),
    }).describe("はみ出し幅選択に使用する設定値。"),
  }),
  generator: z.object({
    guest_join_under_elapsed_time: z.number()
      .describe("ゲストが通話に参加してからの経過時間の上限（ミリ秒）。ゲストポーズを採用するかどうかの判定に使用する。"),
    max_selection_published_count: z.number().min(0)
      .describe("ライブ中の投稿募集回数の上限。投稿募集中ポーズを採用するかどうかの判定に使用する。"),
    selection_published_under_elapsed_time: z.number()
      .describe("最新の投稿募集開始からの経過時間の上限（ミリ秒）。投稿募集中ポーズを採用するかどうかの判定に使用する。"),
    viewer_zero_under_elapsed_time: z.number()
      .describe("同時視聴者数が0人になってからの経過時間の上限（ミリ秒）。視聴者0人ポーズを採用するかどうかの判定に使用する。"),
    guest_follower_times: z.number().min(0)
      .describe("ゲストと判定するフォロワー数の倍率。オーナーのフォロワー数にこの数値を掛けたものがゲストに期待するフォロワー数の下限となる。"),
    momentum_live_count_increase_percentage: z.int().min(0)
      .describe("勢いがあるポーズを採用するために必要な前回の同時視聴者数からの増加率(%)。"),
    momentum_live_count_increase_count: z.number().min(0)
      .describe("勢いがあるポーズを採用するために必要な前回の同時視聴者数からの増加数。"),
    live_start_under_elapsed_time: z.number()
      .describe("ライブ開始からの経過時間の上限（ミリ秒）。ライブ開始ポーズを採用するかどうかの判定に使用する。"),
    live_count_percentage: z.int().min(0)
      .describe("他のイベントに該当しない場合にどのポーズを採用するか判定するために必要な前回の同時視聴者数からの増減率(%)。"),
    look_image_count: z.number().int().min(1).max(3)
      .describe("1回のサムネイル生成で作成するルック画像の枚数。"),
    space_update_interval_ms: z.number().min(0)
      .describe("アクティブなスペースのサムネイル更新間隔（ミリ秒）。"),
    live_update_interval_ms: z.number().min(0)
      .describe("ライブ中のサムネイル更新間隔（ミリ秒）。"),
    space_retry_interval_ms: z.number().min(0)
      .describe("アクティブなスペースのサムネイル更新失敗時のリトライ間隔（ミリ秒）。"),
    live_retry_interval_ms: z.number().min(0)
      .describe("ライブ中のサムネイル更新失敗時のリトライ間隔（ミリ秒）。"),
    max_concurrency: z.number().int().min(1)
      .describe("サムネイル生成daemonの最大並列処理数。STS への同時リクエスト数を制限するために使用する。"),
  }),
})

export type SpaceThumbnailSettingDocument = z.infer<typeof SpaceThumbnailSettingDocument>

export class SpaceThumbnailSettingDocuments {

  static readonly MAX_SPACE_COUNT = 200

  // サムネイルサイズを大・中のみから選ぶ閾値
  // https://www.notion.so/popopo-inc/4352797b807840fca7f61a46ac3172b5?source=copy_link#1dd5e03d57e28006a049d4368974d128
  static readonly THUMBNAIL_SIZE_THRESHOLD_COUNT = 20

  static readonly SPACE_THUMBNAIL_SETTING_ID = "space-thumbnail-setting-v2"

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SpaceThumbnailSettingDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      home: {
        selection_rules: data["home"] ? {
          ...data["home"]["selection_rules"],
          pickup_live_percentage: data["home"]["selection_rules"]["pickup_live_percentage"] ?? 0,
          pickup_live_started_at_percentage: data["home"]["selection_rules"]["pickup_live_started_at_percentage"] ?? 0,
          pickup_live_current_live_count_percentage: data["home"]["selection_rules"]["pickup_live_current_live_count_percentage"] ?? 0,
        } : {
          follow_user_percentage: 0,
          follow_user_started_at_percentage: 0,
          follow_user_current_live_count_percentage: 0,
          pickup_live_percentage: 0,
          pickup_live_started_at_percentage: 0,
          pickup_live_current_live_count_percentage: 0,
          live_count_ranking_count_percentage: 0,
        },
        first_launch: data["home"]?.["first_launch"] ? {
          viewer_count_threshold: data["home"]["first_launch"]["viewer_count_threshold"] ?? 5,
          account_created_within_days: data["home"]["first_launch"]["account_created_within_days"] ?? 90,
          owner_follower_count_min: data["home"]["first_launch"]["owner_follower_count_min"] ?? 100,
          owner_follower_count_max: data["home"]["first_launch"]["owner_follower_count_max"] ?? 700,
        } : undefined,
        thumbnail_size_rules:data["home"] ? data["home"]["thumbnail_size_rules"] : {
          large_percentage: 0,
          medium_percentage: 0,
          small_percentage: 0,
          follow_user_large_percentage: 0,
          follow_user_medium_percentage: 0,
          follow_user_small_percentage: 0,
          live_count_ranking_large_percentage: 0,
          live_count_ranking_medium_percentage: 0,
          live_count_ranking_small_percentage: 0,
          large_count: 0,
          medium_count: 0,
        },
        look_distance_threshold: data["home"] ? data["home"]["look_distance_threshold"] : {
          far: 0,
          near: 0,
        },
        background_effect_intensity_elapsed_time: data["home"] ? data["home"]["background_effect_intensity_elapsed_time"] : {
          live: {
            under: 0,
            over: 0,
          },
          active_space: {
            under: 0,
            over: 0,
          },
        },
        overflow_height: data["home"]?.["overflow_height"] ? {
          power_duration_ms: data["home"]["overflow_height"]["power_duration_ms"] ?? 5 * 60 * 1000,
          total_price_duration_ms: data["home"]["overflow_height"]["total_price_duration_ms"] ?? 60 * 60 * 1000,
          total_price_threshold: data["home"]["overflow_height"]["total_price_threshold"] ?? 30000,
        } : {
          power_duration_ms: 5 * 60 * 1000,
          total_price_duration_ms: 60 * 60 * 1000,
          total_price_threshold: 30000,
        },
      },
      generator: {
        guest_join_under_elapsed_time: data["generator"] ? data["generator"]["guest_join_under_elapsed_time"] : 0,
        max_selection_published_count: data["generator"] ? data["generator"]["max_selection_published_count"] : 0,
        selection_published_under_elapsed_time: data["generator"] ? data["generator"]["selection_published_under_elapsed_time"] : 0,
        viewer_zero_under_elapsed_time: data["generator"] ? data["generator"]["viewer_zero_under_elapsed_time"] : 0,
        guest_follower_times: data["generator"] ? data["generator"]["guest_follower_times"] : 0,
        momentum_live_count_increase_percentage: data["generator"] ? data["generator"]["momentum_live_count_increase_percentage"] : 0,
        momentum_live_count_increase_count: data["generator"] ? data["generator"]["momentum_live_count_increase_count"] : 0,
        live_start_under_elapsed_time: data["generator"] ? data["generator"]["live_start_under_elapsed_time"] : 0,
        live_count_percentage: data["generator"] ? data["generator"]["live_count_percentage"] : 0,
        look_image_count: data["generator"]?.["look_image_count"] ?? 3,
        space_update_interval_ms: data["generator"]?.["space_update_interval_ms"] ?? 10 * 60 * 1000,
        live_update_interval_ms: data["generator"]?.["live_update_interval_ms"] ?? 5 * 60 * 1000,
        space_retry_interval_ms: data["generator"]?.["space_retry_interval_ms"] ?? 10 * 60 * 1000,
        live_retry_interval_ms: data["generator"]?.["live_retry_interval_ms"] ?? 5 * 60 * 1000,
        max_concurrency: data["generator"]?.["max_concurrency"] ?? 3,
      },
    }
  }
}

export const UpdateSpaceThumbnailSettingRequest = z.object({
  generatorLookImageCount: z.coerce.number().int().min(1).max(3).optional(),
  generatorSpaceUpdateIntervalMs: z.coerce.number().min(0).optional(),
  generatorLiveUpdateIntervalMs: z.coerce.number().min(0).optional(),
  generatorSpaceRetryIntervalMs: z.coerce.number().min(0).optional(),
  generatorLiveRetryIntervalMs: z.coerce.number().min(0).optional(),
  generatorMaxConcurrency: z.coerce.number().int().min(1).optional(),
  homeOverflowHeightPowerDurationMs: z.coerce.number().min(0).optional(),
  homeOverflowHeightTotalPriceDurationMs: z.coerce.number().min(0).optional(),
  homeOverflowHeightTotalPriceThreshold: z.coerce.number().min(0).optional(),
})
export type UpdateSpaceThumbnailSettingRequest = z.infer<typeof UpdateSpaceThumbnailSettingRequest>
